import { useEffect, useState } from "react";
import api, { dkk, num } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { ResponsiveContainer, BarChart, Bar, Cell, LabelList, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Clock } from "lucide-react";

const NORMAL = "#38BDF8";
const LATE = "#FB923C";
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const hourLabel = (h) => `${String(h).padStart(2, "0")}`;
const fmtK = (v) => (v == null ? "" : v >= 1000 ? `${(v / 1000).toFixed(1).replace(".", ",")}k` : `${Math.round(v)}`);

function Tip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg bg-[#f8fafc] border border-[#e2e8f0] px-3 py-2 text-xs">
      <p className="text-slate-500 mb-1 font-medium">Kl. {hourLabel(p.hour)}–{hourLabel(p.hour + 1)}</p>
      <p className="text-slate-500">{dkk(p.revenue)}</p>
      <p className="text-slate-500">{num(p.orders)} kunder</p>
    </div>
  );
}

export function DayHourlyCard({ store }) {
  const [day, setDay] = useState(iso(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true; setLoading(true);
    api.get("/kpi/hourly", { params: { from: day, to: day, store } })
      .then(({ data }) => { if (active) setData(data); })
      .catch(() => { if (active) setData(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [day, store]);

  const chart = (data?.hours || []).map((h) => ({ hour: h.hour, revenue: h.avg_revenue, orders: h.avg_orders }));
  const total = chart.reduce((s, h) => s + h.revenue, 0);
  const orders = chart.reduce((s, h) => s + h.orders, 0);
  const activeHours = chart.filter((h) => h.revenue > 0).length;
  const perHour = activeHours ? total / activeHours : 0;

  const label = store === "all" ? "begge butikker" : store;
  const setToday = () => setDay(iso(new Date()));
  const setYesterday = () => { const d = new Date(); d.setDate(d.getDate() - 1); setDay(iso(d)); };

  return (
    <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-5 md:col-span-2 xl:col-span-4" data-testid="day-hourly-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-[13px] font-semibold uppercase tracking-[0.14em] text-slate-500 flex items-center gap-2"><Clock className="w-4 h-4" /> Salg pr. time · {label}</h3>
        <div className="flex items-center gap-2">
          <button onClick={setToday} className="text-xs px-2.5 py-1.5 rounded-lg bg-[#f8fafc] border border-[#e2e8f0] text-slate-500 hover:text-slate-900 transition-colors" data-testid="day-hourly-today">I dag</button>
          <button onClick={setYesterday} className="text-xs px-2.5 py-1.5 rounded-lg bg-[#f8fafc] border border-[#e2e8f0] text-slate-500 hover:text-slate-900 transition-colors" data-testid="day-hourly-yesterday">I går</button>
          <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="h-9 w-[150px] rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800" data-testid="day-hourly-date" />
        </div>
      </div>

      {loading ? (
        <p className="text-slate-500 text-sm py-10 text-center" data-testid="day-hourly-loading">Henter…</p>
      ) : chart.length === 0 ? (
        <p className="text-slate-500 text-sm py-10 text-center" data-testid="day-hourly-empty">Ingen salg registreret for den valgte dag.</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-8 mb-5">
            <div>
              <div className="text-xs text-slate-500">Omsætning</div>
              <div className="font-heading text-2xl font-light tabular-nums text-slate-900" data-testid="day-hourly-total">{dkk(total)}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Kunder</div>
              <div className="font-heading text-2xl font-light tabular-nums text-slate-900" data-testid="day-hourly-orders">{num(orders)}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Gns. pr. time ({activeHours} t)</div>
              <div className="font-heading text-2xl font-light tabular-nums text-sky-600" data-testid="day-hourly-perhour">{dkk(perHour)}</div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chart} margin={{ top: 22, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="hour" tickFormatter={hourLabel} tick={{ fontSize: 11, fill: "#64748B" }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickLine={false} axisLine={false} width={52} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
              <Tooltip content={<Tip />} cursor={{ fill: "#ffffff08" }} />
              <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
                {chart.map((h) => <Cell key={h.hour} fill={h.hour >= 17 ? LATE : NORMAL} />)}
                <LabelList dataKey="revenue" position="top" formatter={fmtK} fill="#94A3B8" fontSize={10} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 mt-3">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: NORMAL }} /> Dagtimer</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: LATE }} /> Aften (efter 17)</span>
            <span>Tal over søjlen = salg i den time</span>
          </div>
        </>
      )}
    </div>
  );
}
